import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { io, Socket } from 'socket.io-client';

import { environment } from '@environments/environment';

// Store
import { accountDeleted, setSocketToken } from '@store/profile/profile.actions';
import { updateWidgetPhone } from '@store/widget/widget.actions';

// Interfaces
import { IWebSocketDataEvent } from './web-socket.interfaces';
import { ITranslateData } from '@interfaces/transcription.interfaces';
import { IWhiteboardData } from '@interfaces/whiteboard.interfaces';

// Enums
import { EWebSocketEvent } from './web-socket.enums';

// Config
import {
  CWebSocketConfig,
  CWebSocketPingInterval,
  CWebSocketPongWaitingInterval,
} from './web-socket.config';

/**
 * A service for managing WebSocket connections with the server.
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketsService {
  private httpClient = inject(HttpClient);
  private store = inject(Store);

  /** A subject that emits when it's time to destroy the timer. */
  private destroyTimer$: Subject<void> = new Subject<void>();
  /** A subject that emits when it's time to destroy the events. */
  private destroyEvents$: Subject<void> = new Subject<void>();
  /** The WebSocket connection instance. */
  private socket$: Socket | null = null;
  /** The channel name of the user. */
  private userChannelName: string | null = null;

  /** A subject that emits messages received from the WebSocket. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public messagesSubject$: BehaviorSubject<IWebSocketDataEvent<any> | null> =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new BehaviorSubject<IWebSocketDataEvent<any> | null>(null);
  /** A subject that emits after web-socket reconnect. */
  public reconnectSubject$: Subject<void> = new Subject<void>();

  getSocketToken(): string | undefined {
    return this.socket$?.auth?.['token'];
  }

  getUserChannelName(): string | null {
    return this.userChannelName;
  }

  /**
   * Makes a request to the server to get the user's authorization token for the web socket connection.
   */
  createConnection(): void {
    const { ws_channel_name } = environment;
    this.httpClient.get<{ socket_token: string; status: string }>('/socket/auth').subscribe({
      next: ({ socket_token }) => {
        this.userChannelName = ws_channel_name + socket_token;
        this.createSocket(socket_token);
      },
      error: error => {
        console.error('Create WebSocket connection error:', error);
      },
    });
  }

  /**
   * Creates a new WebSocket instance.
   * @param {string} token - The user token used for authentication.
   * @return {Socket} The WebSocket instance.
   */
  createSocket(token: string): Socket | null {
    if (this.socket$?.connected) return null;
    this.socket$ = io(environment.ws_url, {
      ...CWebSocketConfig,
      auth: { token, type: 'is_web_user' },
    });
    this.store.dispatch(setSocketToken({ token }));
    this.subscribeForEvents();
    this.initPingEvent();
    return this.socket$;
  }

  /**
   * Destroy the WebSocket instance & unsubscribe from events.
   */
  destroySocket(): void {
    this.destroyEvents$.next();
    this.destroyTimer$.next();
    if (this.socket$) {
      this.socket$.disconnect();
    }
    this.unsubscribeFromEvents();
  }

  /**
   * Disconnects the WebSocket instance from the server.
   */
  disconnectSocket(): void {
    this.destroySocket();
    this.messagesSubject$.next(null);
  }

  /**
   * Reconnects the WebSocket instance to the server.
   */
  reconnectSocket(): void {
    this.destroySocket();
    this.socket$.connect();
    this.reconnectSubject$.next();
    this.subscribeForEvents();
    this.initPingEvent();
  }

  /**
   * Initializes the ping event to keep the WebSocket connection alive.
   */
  initPingEvent(): void {
    interval(CWebSocketPingInterval)
      .pipe(takeUntil(this.destroyEvents$))
      .subscribe({
        next: () => {
          this.socket$.emit(EWebSocketEvent.Ping);
          this.startPongWaitingTimer();
        },
      });
  }

  /**
   * Starts the pong waiting timer.
   */
  startPongWaitingTimer(): void {
    interval(CWebSocketPongWaitingInterval)
      .pipe(takeUntil(this.destroyTimer$))
      .subscribe({
        next: () => {
          this.reconnectSocket();
        },
      });
  }

  /**
   * Handles the 'Connect' event of the web socket.
   */
  onConnectEvent(): void {
    console.info('Socket successfully connected!');
  }

  /**
   * Handles incoming data events from the web socket.
   * @param {IWebSocketDataEvent} dataEvent - The data received from the web socket by user channel.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDataEvent(dataEvent: IWebSocketDataEvent<any>): void {
    if (!Object.keys(dataEvent).length) {
      return;
    }
    this.messagesSubject$.next(dataEvent);
    if (dataEvent.type === 'widget-phone-update') {
      this.store.dispatch(updateWidgetPhone({ data: dataEvent.data }));
    }
    if (dataEvent.type === 'account-deleted') {
      this.store.dispatch(accountDeleted());
    }
  }

  /**
   * Handles incoming translation transcription data events from the web socket.
   * @param {IWebSocketDataEvent} dataEvent - The data received from the web socket by user channel.
   */
  onTranscriptionsWithTranslateEvent(dataEvent: IWebSocketDataEvent<ITranslateData>): void {
    if (!Object.keys(dataEvent).length) {
      return;
    }
    this.messagesSubject$.next(dataEvent);
  }

  /**
   * Handles incoming whiteboard data events from the web socket.
   * @param {IWebSocketDataEvent} dataEvent - The data received from the web socket by user channel.
   */
  onWhiteboardEvent(dataEvent: IWebSocketDataEvent<IWhiteboardData>): void {
    if (!Object.keys(dataEvent).length) {
      return;
    }
    this.messagesSubject$.next(dataEvent);
  }

  /**
   * Handles connect to room event from the web socket.
   * @param {string} _dataEvent - The message from the web socket by user channel.
   */
  onConnectToRoomEvent(_dataEvent: { type: string; data: { message: string } }): void {
    console.info('Connected to Room!');
  }

  /**
   * Handles the 'Pong' event of the web socket.
   */
  onPongEvent(): void {
    this.destroyTimer$.next();
  }

  /**
   * Handles the send action of the web socket.
   */
  onSendMessage(eventName: string, body: unknown): void {
    this.socket$?.emit(eventName, body);
  }

  /**
   * Subscribes to events of the web socket.
   */
  subscribeForEvents(): void {
    this.socket$?.on(EWebSocketEvent.Connect, this.onConnectEvent.bind(this));
    this.socket$?.on(EWebSocketEvent.ConnectToRoom, this.onConnectToRoomEvent.bind(this));
    this.socket$?.on(EWebSocketEvent.Pong, this.onPongEvent.bind(this));
    this.socket$?.on(this.userChannelName, this.onDataEvent.bind(this));
    this.socket$?.on(
      EWebSocketEvent.TranscriptionsWithTranslate,
      this.onTranscriptionsWithTranslateEvent.bind(this),
    );
    this.socket$?.on(EWebSocketEvent.Whiteboard, this.onWhiteboardEvent.bind(this));
  }

  /**
   * Unsubscribes from events of the web socket.
   */
  unsubscribeFromEvents(): void {
    this.socket$?.removeListener(EWebSocketEvent.Connect, this.onConnectEvent.bind(this));
    this.socket$?.removeListener(
      EWebSocketEvent.ConnectToRoom,
      this.onConnectToRoomEvent.bind(this),
    );
    this.socket$?.removeListener(EWebSocketEvent.Pong, this.onPongEvent.bind(this));
    this.socket$?.removeListener(this.userChannelName, this.onDataEvent.bind(this));
    this.socket$?.removeListener(
      EWebSocketEvent.TranscriptionsWithTranslate,
      this.onTranscriptionsWithTranslateEvent.bind(this),
    );
    this.socket$?.removeListener(EWebSocketEvent.Whiteboard, this.onWhiteboardEvent.bind(this));
  }
}
